import { useEffect, useRef } from 'react';
import { useGameStore } from '@/state/gameStore';
import { fetchRoute, computeLegDurationMs } from '@/lib/routing';
import { VEHICLE_SPECS } from '@/data/vehicles';
import { pickNearestAvailableHospital } from '@/state/vehicleStateMachine';
import type { Vehicle, VehicleStatus } from '@/state/types';

const TRANSIT_STATUSES = new Set<VehicleStatus>([1, 3, 7]);
const HOSPITAL_RECHECK_MS = 30_000;

/**
 * Watches every transit-status vehicle (Status 1/3/7) and ensures it has a
 * current route + leg duration set. Called once from `<Game>`. Runs async
 * fetches outside the tick reducer so the store stays sync.
 *
 * Behaviour per status:
 *   - 3: route from home station → mission position
 *   - 7: pick nearest hospital with free capacity → route there.
 *        If no hospital free, the vehicle parks (no route), tick re-checks
 *        every HOSPITAL_RECHECK_MS gameTime via the dependency list.
 *   - 1: route from current vehicle.position → home station
 *
 * Reload behaviour: `onRehydrateStorage` clears `routeCoords/legStartedAt/
 * legDurationMs` for all vehicles. This hook then re-fetches and resets
 * `legStartedAt = currentGameTime`.
 */
export function useVehicleRouting() {
  const vehicles = useGameStore((s) => s.vehicles);
  const stations = useGameStore((s) => s.stations);
  const missions = useGameStore((s) => s.missions);
  const updateVehicle = useGameStore((s) => s.updateVehicle);
  const pushToast = useGameStore((s) => s.pushToast);

  // Vehicle ids currently being routed (avoid duplicate fetches in a flight).
  const inFlight = useRef<Set<string>>(new Set());
  // Last "no-hospital-free" toast time per vehicle (gameTime).
  const noHospitalToasted = useRef<Map<string, number>>(new Map());
  // Per-vehicle last hospital re-check time.
  const lastRechecked = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    let cancelled = false;

    for (const v of vehicles) {
      if (!TRANSIT_STATUSES.has(v.status)) continue;
      if (v.routeCoords && v.legStartedAt !== undefined && v.legDurationMs !== undefined) continue;
      if (inFlight.current.has(v.id)) continue;

      const handle = async (vehicle: Vehicle) => {
        const gs = useGameStore.getState();
        const station = gs.stations.find((s) => s.id === vehicle.stationId);
        if (!station) return;

        let from = vehicle.position;
        let to: { lat: number; lng: number } | null = null;
        let legType: 'emergency' | 'return' = 'emergency';
        let chosenHospitalId: string | undefined;

        if (vehicle.status === 3) {
          // Emergency: station → mission
          const mission = vehicle.assignedMissionId
            ? gs.missions.find((m) => m.id === vehicle.assignedMissionId)
            : null;
          if (!mission) return;
          // After alarm, the vehicle is conceptually at the station; use station pos for the leg start.
          from = station.position;
          to = mission.position;
          legType = 'emergency';
        } else if (vehicle.status === 7) {
          // Transport: scene → nearest free hospital
          const hospitals = gs.stations.filter((s) => s.kind === 'hospital');
          const target = pickNearestAvailableHospital(hospitals, vehicle.position, gs.gameTime);
          if (!target) {
            // Throttle the "no hospital" toast to once per vehicle per recheck window.
            const lastT = noHospitalToasted.current.get(vehicle.id) ?? 0;
            if (gs.gameTime - lastT > HOSPITAL_RECHECK_MS) {
              pushToast({
                kind: 'warning',
                text: `Kein freies Krankenhaus verfügbar – Patient wartet im ${VEHICLE_SPECS[vehicle.type].label}.`,
              });
              noHospitalToasted.current.set(vehicle.id, gs.gameTime);
            }
            return;
          }
          to = target.position;
          chosenHospitalId = target.id;
          legType = 'emergency';
        } else if (vehicle.status === 1) {
          // Return: current pos → station
          to = station.position;
          legType = 'return';
        }

        if (!to) return;

        inFlight.current.add(vehicle.id);
        try {
          const spec0 = VEHICLE_SPECS[vehicle.type];
          // Phase 10.2 — Air units skip OSRM, draw a straight line instead.
          const route = spec0.airUnit
            ? (() => {
                const dx = (to.lat - from.lat) * 111.32;
                const dy = (to.lng - from.lng) * 111.32 * Math.cos((from.lat * Math.PI) / 180);
                const distanceKm = Math.sqrt(dx * dx + dy * dy);
                return {
                  coords: [
                    [from.lng, from.lat] as [number, number],
                    [to.lng, to.lat] as [number, number],
                  ],
                  distanceKm,
                  fromOsrm: false,
                };
              })()
            : await fetchRoute(from, to);
          if (cancelled) return;

          // Re-read latest state — vehicle might have changed status mid-fetch.
          const fresh = useGameStore.getState().vehicles.find((x) => x.id === vehicle.id);
          if (!fresh) return;
          if (!TRANSIT_STATUSES.has(fresh.status)) return;
          if (fresh.routeCoords) return;

          const spec = VEHICLE_SPECS[fresh.type];
          const durationMs = computeLegDurationMs(
            route.distanceKm,
            spec.speedMultiplier,
            legType,
            route.fromOsrm,
          );
          const now = useGameStore.getState().gameTime;

          updateVehicle(fresh.id, {
            routeCoords: route.coords,
            legStartedAt: now,
            legDurationMs: durationMs,
            legDistanceKm: route.distanceKm,
            destinationHospitalId: chosenHospitalId ?? fresh.destinationHospitalId,
          });
        } finally {
          inFlight.current.delete(vehicle.id);
        }
      };

      void handle(v);
    }

    return () => {
      cancelled = true;
    };
  }, [vehicles, stations, missions, updateVehicle, pushToast]);

  // Periodic hospital re-check for parked Status-7 vehicles. The dependency on
  // `vehicles` already triggers re-evaluation on every tick. We use a simple
  // ref to avoid spamming.
  useEffect(() => {
    const gs = useGameStore.getState();
    for (const v of vehicles) {
      if (v.status !== 7) continue;
      if (v.routeCoords) continue; // already has a hospital route
      const last = lastRechecked.current.get(v.id) ?? 0;
      if (gs.gameTime - last < HOSPITAL_RECHECK_MS) continue;
      lastRechecked.current.set(v.id, gs.gameTime);
      // The main effect above will re-run on next vehicle update; nothing to do here directly.
    }
  }, [vehicles]);
}
